import { segment } from '@actions/analytics';
import H7 from '@base/H7/H7';
import Image from '@base/Image';
import {
  GROUP_PROFILE_ACTIVITY_CLICKED,
  PLANNER_PAGE
} from '@constants/segment';
import NotAvailableDialog from '@cont/Planner/PlannerCards/NotAvailableDialog';
import { PRACTICE_TYPE } from '@cont/Practice/constants';
import { GroupsContext } from '@context/GroupsContext';
import styled from '@emotion/styled';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';
import { Spacings } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import * as React from 'react';
import { MouseEvent } from 'react';
import {
  GroupObjectType,
  ILatestActivity,
  IPracticingActivity
} from 'types/groups';
import useCheckFeatureLimit, {
  FeatureObjectType
} from '@cont/DeviceLimitation/useCheckFeatureLimit';
import useFetch from '@hooks/useFetch';
import usePracticeStore from '@stores/practice';
import { ActivityObjectTypeInfo } from '../../groupsConstants';
import {
  computeLatestActivityImage,
  computeLatestActivityTitle
} from './computeLastestActivityTitle';

const GroupObjectTypeValue = {
  [GroupObjectType.IN_CLASS]: 'class',
  [GroupObjectType.TAKING_A_TEST]: 'test',
  [GroupObjectType.TAKING_A_QUIZ]: 'quiz',
  [GroupObjectType.COMBAT]: 'combat',
  [GroupObjectType.READING_NOTES]: 'notes',
  [GroupObjectType.PRACTICING]: 'practice'
};

const LatestActivityWrapper = styled.div`
  display: grid;
  grid-template-columns: ${Spacings.SPACING_6B} 1fr;
  grid-gap: ${Spacings.SPACING_2B};
  align-items: center;
  padding: ${Spacings.SPACING_2B};
  padding-right: ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_12B};
  border: ${Spacings.SPACING_1} solid ${({ color }) => color};
  margin-bottom: ${Spacings.SPACING_3B};
  max-width: 220px;
  cursor: pointer;
`;

const StyH7 = styled(H7)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export interface ILatestActivityProps {
  latestActivity: ILatestActivity;
  closeDrawerAndPopup: () => void;
}

const getGroupsItemInfoKey = (shouldOpenItem, activityType, itemUID) => {
  if (!shouldOpenItem) return null;

  return activityType === GroupObjectType.IN_CLASS
    ? `v1/uplus/classes/${itemUID}/info_v2/`
    : `v2/quizzes/planner/${itemUID}/info/`;
};

const ProfilePopupContent: React.FC<ILatestActivityProps> = ({
  latestActivity,
  closeDrawerAndPopup
}) => {
  const activityType: GroupObjectType = latestActivity.objectType;
  const activityTitle = computeLatestActivityTitle(latestActivity);
  const activityImage = computeLatestActivityImage(latestActivity);
  const groupsContext = React.useContext(GroupsContext);
  const { selectedGoal, users: usersList } = groupsContext;
  const goalUID = groupsContext.selectedGoalUID;
  const itemUID = latestActivity.data?.uid;
  const goalSegmentData = useGoalEventProps({ goalUID });
  // @ts-ignore
  const { setPracticeSelectedTopic } = usePracticeStore();

  const [shouldOpenItem, setShouldOpenItem] = React.useState(false);
  const [showUnavailableDialog, setShowUnavailableDialog] =
    React.useState(false);

  const { open: openClassPlayer } = useCourseClassPlayer();
  const { data: itemInfo, isLoading } = useFetch(
    getGroupsItemInfoKey(shouldOpenItem, activityType, itemUID),
    {
      headers: {
        'X-Direct-To': 'sheldon',
        'X-PAGE': 'GROUPS'
      }
    },
    {}
  );

  const isItemInfoLoaded = !isLoading && !!itemInfo;

  const handleHideUnavailableDialog = () => setShowUnavailableDialog(false);
  const handleShowUnavailableDialog = () => setShowUnavailableDialog(true);

  const handleClassItemClick = useCheckFeatureLimit(
    FeatureObjectType.CLASS,
    () => {
      const item = itemInfo;
      openClassPlayer({
        lesson: item,
        course: item.programme?.course || item.programme,
        byPassCheck: true,
        forceFetch: true
      });
    }
  );

  if (shouldOpenItem && isItemInfoLoaded) {
    setShouldOpenItem(false);
    const item = itemInfo;
    closeDrawerAndPopup();
    if (activityType === GroupObjectType.IN_CLASS) {
      handleClassItemClick();
    } else if (
      activityType === GroupObjectType.TAKING_A_QUIZ ||
      activityType === GroupObjectType.TAKING_A_TEST
    ) {
      pushRoute({
        pathname: '/quiz/[quizSlug]/[quizUID]',
        query: { quizSlug: item.slug, quizUID: item.uid }
      });
    }
  }

  const handleActivityClick = (ev: MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    segment.track(GROUP_PROFILE_ACTIVITY_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: usersList.length,
      type: GroupObjectTypeValue[activityType]
    });
    if (
      [
        GroupObjectType.IN_CLASS,
        GroupObjectType.TAKING_A_QUIZ,
        GroupObjectType.TAKING_A_TEST
      ].includes(activityType)
    ) {
      setShouldOpenItem(true);
    } else if (activityType === GroupObjectType.PRACTICING) {
      const { topicGroupUid: topicUID, topicGroupName: topicName } = (
        latestActivity as IPracticingActivity
      ).data;
      closeDrawerAndPopup();
      setPracticeSelectedTopic({
        type: PRACTICE_TYPE.FULL,
        parentUID: topicUID,
        parentName: topicName,
        name: topicName,
        uid: topicUID
      });
    } else if (activityType === GroupObjectType.READING_NOTES) {
      handleShowUnavailableDialog();
    } else if (activityType === GroupObjectType.COMBAT) {
      pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/test-series',
        query: { goalSlug: selectedGoal.goalSlug, goalUID, to: 'combat' }
      });
    }
  };

  return (
    <>
      {showUnavailableDialog && (
        <NotAvailableDialog
          onClose={handleHideUnavailableDialog}
          eventType={activityType}
        />
      )}
      <LatestActivityWrapper
        onClick={handleActivityClick}
        color={ActivityObjectTypeInfo[activityType]?.color}
      >
        <Image
          src={activityImage}
          height={Spacings.SPACING_6B}
          width={Spacings.SPACING_6B}
          alt="activity"
        />
        <StyH7 data-testid="groups-user-activity-title">{activityTitle}</StyH7>
      </LatestActivityWrapper>
    </>
  );
};

export default ProfilePopupContent;
